import { globalStyles } from './globals';

export const fonts = [
  'https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap',
];

/**
 * This constant was created to have a single source of truth within this file
 * for the primary, secondary, and tertiary colors for each branding.
 * These three colors are the most important for each branding.
 * These colors are defined in the brandbook of each partner.
 */
const BASE_COLORS = {
  brandPrimary: '#DC002B',
  brandSecondary: '#750A22',
  brandTertiary: '#8F1C5F',
} as const;

export const styles = {
  ...globalStyles,

  // Brand Color
  colorBrandPrimary: '#DC002B',
  colorBrandSecondary900: '#750A22',
  colorBrandSecondary400: '#970725',
  colorBrandSecondary100: '#BA0328',

  // ---- Buttons ----

  // Primary
  colorButtonPrimaryDefault: '#DC002B',
  colorButtonPrimaryHover: '#750A22',

  // Secondary
  colorButtonSecondaryDefault: '#DC002B',
  colorButtonSecondaryHover: '#DC002B',

  // Tertiary
  colorButtonTertiaryDefault: '#E5E7EB',
  colorButtonTertiaryHover: '#F3F4F6',
  colorButtonTertiaryBorder: '#E5E7EB',

  // Danger
  colorButtonDangerDefault: '#C81E1E',
  colorButtonDangerHover: '#9B1C1C',

  // ---- Inputs ----
  colorInputBorderDefault: '#d1d5dc', // gray-300
  // Form-only focus color (UES-12339) — must match colorFocusForm
  colorInputBorderFocused: '#2563EB',
  colorInputBorderError: '#C10007', // Tailwind red-700 (was red-900)
  colorInputBorderDisabled: '#F7F7F7',
  colorInputBackgroundDisabled: '#F7F7F7',

  colorInputDropdownTextDefault: '#677b88',

  colorInputTogglePrimary: '#14af6e',
  colorInputToggleSecondary: '#DC002B',

  // ----- Charts -----

  // Billing
  colorGraphCategory1: '#5AA09B',
  colorGraphCategory2: '#750A22',
  colorGraphCategory3: '#935E96',
  colorGraphCategory4: '#DC8193',
  colorGraphCategory5: '#878787',
  colorGraphCategory6: '#DD6605',
  colorGraphCategory7: '#E74D6B',
  colorGraphCategory8: '#1089BF',
  colorGraphCategory9: '#1089BF',
  colorGraphCategory10: '#DD6605',

  colorGraphFailure1: '#C22222',
  colorGraphFailure2: '#D63535',
  colorGraphFailure3: '#E05656',
  colorGraphFailure4: '#E66969',
  colorGraphFailure5: '#EB9090',

  colorGraphExpired1: '#E0529A',
  colorGraphExpired2: '#FF69B4',
  colorGraphExpired3: '#FF8CC5',
  colorGraphExpired4: '#FFA3CF',
  colorGraphExpired5: '#FFB9DA',

  colorGraphSuperseded1: '#E57A32',
  colorGraphSuperseded2: '#FF914D',
  colorGraphSuperseded3: '#FFB07A',
  colorGraphSuperseded4: '#FFCBA6',
  colorGraphSuperseded5: '#FFE2C7',

  colorGraphIneligible1: '#6B7280',
  colorGraphIneligible2: '#9CA3AF',
  colorGraphIneligible3: '#D1D5DB',

  colorGraphBilling1: '#750A22',
  colorGraphBilling2: '#e75672',

  colorGraphCategoryError1: '#A83232',
  colorGraphCategoryError2: '#D9534F',

  // --- On Boarding ----

  // Journey
  colorOnboardingJourneyBg: '#750A22D9',
  colorOnboardingJourneyText: '#FFFFFF',
  colorOnBoardingJourneyNumberBg: '#dd8e9c',

  // Prelude
  colorOnBoardingPreludeBg: '#750A22',

  // Backgrounds
  colorBackgroundPrimary: '#F0F0F0',
  colorBackgroundHighlight: '#094371',
  colorBackgroundDashboardLinks: '#cccccc',

  // Fonts
  // Tailwind gray-900 — headings/primary text (design direction 2026-07)
  colorFontPrimary: '#101828',
  colorFontSecondary: '#eee',

  // typography
  fontLight: "'Inter', sans-serif",
  fontStack: "'Inter', sans-serif",
  fontBold: "'Inter', sans-serif",
  fontBook: "'Inter', sans-serif",
  fontOsRegular: "'Inter', sans-serif",
  fontOsSemibold:
    "'OpenSans-SemiBold', 'Roboto', Tahoma, Helvetica, Arial, sans-serif",
  fontReceipt:
    "'Roboto-Regular', 'OpenSans-Regular', Tahoma, Helvetica, Arial, sans-serif",
  fontReceiptBold:
    "'Roboto-Bold', 'OpenSans-SemiBold', Tahoma, Helvetica, Arial, sans-serif",
  fontLanguageBold:
    "'SegoeUI-Bold', 'OpenSans-SemiBold', Tahoma, Helvetica, Arial, sans-serif",

  // Components

  // TestTable
  colorTestTableActiveBgItem: '#C3C8C8',
  colorTestTableActiveBorderItem: '#878787',
  colorTestTableActiveTextItem: '#000000',

  // Accordion
  colorAccordionActiveBgItem: '#C3C8C8',

  // Side Menu
  colorSideMenuActiveBgItem: '#F0F0F0',

  // Language Selector
  colorBgLanguageSelectorChip: '#F0F0F0',
  colorBgLanguageSelectorSelectedOption: '#F0F0F0',

  // Select
  colorBgSelectSelected: '#F0F0F0',

  // WikiBox
  colorWikiBoxMenuBg: '#DC002B1A',
  colorWikiBoxMenuTitle: '#000000',
  colorWikiBoxMenuText: '#DC002B',

  // Developers Tables
  colorDocTableCellTitle: '#FFFFFF',

  // Portal UI Colors
  colorSecondary: BASE_COLORS.brandSecondary,
  colorTertiary: BASE_COLORS.brandTertiary,
  disabledButtonBg: BASE_COLORS.brandTertiary,

  // New variables
  borderColor: '#082E4D',
  colorPrimaryHover: '#2E1B46',
  // Keyboard focus ring — must keep >= 3:1 contrast on white (WCAG 1.4.11)
  colorFocus: BASE_COLORS.brandPrimary,
  // Form components only (focus-ring-form utility): blue-600, 5.2:1 on white
  colorFocusForm: '#2563EB',
  bgPrimary: '#e6eeee',
  bgPrimaryHover: '#e6eeee',
  colorSecondaryHover: '#7f37b1',
  bgSecondary: '#3C0977',
  bgSecondaryHover: '#320763',
  colorTertiaryHover: '#06411F',
  bgTertiary: '#F0F0F0',
  bgTertiaryHover: '#A5B1B8',
  uiTexticonLight: '#FFFFFF',
  uiBgDark: '#970725',
  uiBgLight: '#fff',
  uiBgHeader: '#C3C8C8',
  brand01: '#924DC2',
  brand02: '#035827',
  brand03Blue: BASE_COLORS.brandPrimary,
  developerDoc: '#e8eef9',
  inactiveMenu: '#5e656c',
  blue4: '#B4E1E7',

  // Variables
  indigo500: '#7568d8',
  blueMint: '#c1e4f1',
  grayPorcelain: '#EAECEE',
  loadingColor: BASE_COLORS.brandPrimary,
  ellipsisHighlightColor: '#48042B',
  ellipsisHighlightColorLight: BASE_COLORS.brandPrimary,
  ellipsisColor: '#0A3A61',
  selectedBlue: '#016F2F',
  purple: '#8F1C5F',

  // Auth
  authBg: '#fff',
  backgroundAuthSize: 'cover',
  backgroundAuthPosition: 'right',
  backgroundAuthRepeat: 'no-repeat',

  // Checkboxes Colors
  checkboxDefault: '#3A474E',
  checkboxPrimary: BASE_COLORS.brandPrimary,

  // Loader Colors
  apiCardPrimaryBg: '#750A22',
  apiCardPrimaryStart: '#DC002B',
  apiCardPrimaryStop: '#970725',
  apiCardSecondaryBg: '#750A22',
  apiCardSecondaryStart: '#DC002B',
  apiCardSecondaryStop: '#970725',
  apiCardTertiaryBg: '#750A22',
  apiCardTertiaryStart: '#DC002B',
  apiCardTertiaryStop: '#970725',

  qualityServiceLevel1: '#E6EEEE',
  qualityServiceLevel2: '#DFEDED',
  qualityServiceLevel3: '#CB4141',
  qualityServiceTooltipBg: '#fff',
  qualityServiceTooltipColor: '#000',

  // System Colors
  deviceSystemBlack: '#1C1C1E',
  informative: '#007BC4',
};
